// Past-due notice shown on every dashboard load.
//   grace     → closable (reappears on the next load); a courtesy reminder.
//   suspended → hard block: not dismissible, no other action possible — EXCEPT
//               a LOXE super-admin, who can close it to keep working.
// Prompts the customer to pay the open renewal invoice.

import React, { useEffect, useState } from "react";
import { Layer, Box, Heading, Text, Button } from "grommet";
import { useTranslation } from "react-i18next";
import BillingService from "../../services/billing.service";
import { useSetCurrentUser } from "./redux/hooks";

function formatMoney(amount, currency) {
  if (amount == null) return "";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
      currencyDisplay: "code",
    }).format(amount);
  } catch {
    return `${amount}`;
  }
}

export default function BillingGraceModal() {
  const { t } = useTranslation();
  const { isLoxeAdmin } = useSetCurrentUser();
  const [renewal, setRenewal] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    BillingService.getBillingInfo()
      .then((res) => {
        if (!mounted) return;
        const r = res.data && res.data.renewal;
        if (r && (r.status === "grace" || r.status === "suspended")) {
          setRenewal(r);
          setOpen(true);
        }
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  if (!open || !renewal) return null;

  const suspended = renewal.status === "suspended";
  // Suspended is a hard block; only a LOXE super-admin may dismiss it. Grace is
  // always dismissible (it reappears on the next page load).
  const canClose = !suspended || isLoxeAdmin;

  const openInvoice = (renewal.invoices || []).find(
    (i) => i.status !== "Paid" && i.status !== "Cancelled",
  );

  const dismiss = canClose ? () => setOpen(false) : undefined;

  return (
    <Layer
      modal
      onEsc={dismiss}
      onClickOutside={dismiss}
      responsive={false}
      background={{ color: "white" }}
    >
      <Box pad="large" gap="medium" width={{ max: "560px" }}>
        <Heading
          level={2}
          margin="none"
          color={suspended ? "#ff4040" : "#FFAA15"}
        >
          {t(suspended ? "graceModal.suspendedTitle" : "graceModal.graceTitle")}
        </Heading>
        <Text size="large">
          {t(
            suspended
              ? "graceModal.suspendedMessage"
              : "graceModal.graceMessage",
          )}
        </Text>

        {openInvoice && (
          <Box
            border={{ color: "border" }}
            round="small"
            pad="medium"
            gap="small"
            background="light-1"
          >
            <Text weight="bold">{t("graceModal.invoiceLabel")}</Text>
            <Box direction="row" justify="between" align="center">
              <Text>{openInvoice.invoiceNumber}</Text>
              <Text weight="bold">
                {formatMoney(openInvoice.total, renewal.currency)}
              </Text>
            </Box>
            <Box direction="row" gap="small" margin={{ top: "small" }}>
              {openInvoice.hostedInvoiceUrl && (
                <Button
                  primary
                  label={t("graceModal.pay")}
                  onClick={() =>
                    window.open(openInvoice.hostedInvoiceUrl, "_blank")
                  }
                />
              )}
              {openInvoice.pdfUrl && (
                <Button
                  label={t("graceModal.download")}
                  onClick={() => window.open(openInvoice.pdfUrl, "_blank")}
                />
              )}
            </Box>
          </Box>
        )}

        {canClose && (
          <Box direction="row" justify="end">
            <Button
              label={t("graceModal.close")}
              onClick={() => setOpen(false)}
            />
          </Box>
        )}
      </Box>
    </Layer>
  );
}
